import React from 'react';
import {
  Shield, CreditCard, CheckCircle, AlertTriangle,
  Clock, Package
} from 'lucide-react';

const EscrowActions = ({
  activeEscrow,
  isCurrentUserBuyer,
  isCurrentUserSeller,
  processingAction,
  handleConfirmReceipt,
  handlePayIntoEscrow,
  navigate,
  conversationId,
  listingTitle,
  sellerStoreName,
  sellerName,
  sellerId,
  buyerId, // Add buyerId prop
  amount, // Add amount prop
  locationState // Add location state if needed
}) => {
  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (!activeEscrow) {
    return (
      <div className="space-y-3">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {isCurrentUserBuyer ? 'Secure Payment Available' : 'Awaiting Payment'}
              </p>
              <p className="text-lg font-bold text-gray-900">
                {isCurrentUserBuyer ? 'Escrow Protection' : 'Escrow Payment'}
              </p>
            </div>
            <Shield className={`w-8 h-8 ${isCurrentUserBuyer ? 'text-green-600' : 'text-gray-400'}`} />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {isCurrentUserBuyer
              ? 'Pay securely through escrow. Funds are held until you confirm receipt.'
              : 'Buyer needs to complete payment to activate escrow protection.'}
          </p>
        </div>
        {isCurrentUserBuyer && (
          <button
            onClick={handlePayIntoEscrow}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Shield className="w-5 h-5" />
            Pay into Escrow
          </button>
        )}
      </div>
    );
  }
  
  switch(activeEscrow.status) {
    case 'pending':
      return (
        <div className="space-y-3">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800">
                  {isCurrentUserBuyer ? 'Awaiting Your Payment' : 'Awaiting Buyer Payment'}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {isCurrentUserBuyer
                    ? 'Complete the payment to secure funds in escrow'
                    : 'Waiting for buyer to complete payment'}
                </p>
              </div>
            </div>
          </div>
          {isCurrentUserBuyer && (
            <button
              onClick={() => navigate(`/chats/${conversationId}/escrow-payment`, {
                state: {
                  escrowId: activeEscrow.escrow_id,
                  listingTitle: listingTitle || 'Item',
                  sellerName: sellerStoreName || sellerName,
                  sellerId: sellerId
                }
              })}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Complete Payment
            </button>
          )}
        </div>
      );
      
    case 'held':
      if (isCurrentUserBuyer) {
        return (
          <div className="space-y-3">
            <button
              onClick={handleConfirmReceipt}
              disabled={processingAction}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-5 h-5" />
              {processingAction ? 'Processing...' : 'Confirm Receipt & Release Payment'}
            </button>
            
            {/* UPDATED: Navigate to dispute raise page */}
            <button
              onClick={() => navigate(`/disputes/raise`, {
                state: {
                  escrowId: activeEscrow.escrow_id,
                  conversationId: conversationId,
                  amount: activeEscrow.amount,
                  sellerId: sellerId,
                  buyerId: buyerId,
                  listingTitle: listingTitle || 'Item',
                  fromChat: true,
                  chatState: locationState
                }
              })}
              disabled={processingAction}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <AlertTriangle className="w-5 h-5" />
              Raise Dispute
            </button>
          </div>
        );
      } else {
        // Seller view - sellers can also raise disputes
        return (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Ready to Ship</p>
                  <p className="text-sm text-green-700 mt-1">
                    Ensure buyer Confirm Receipt & Release Payment when transaction is completed.
                  </p>
                </div>
              </div>
            </div>
            
            {/* UPDATED: Sellers can also raise disputes */}
            <button
              onClick={() => navigate(`/disputes/raise`, {
                state: {
                  escrowId: activeEscrow.escrow_id,
                  conversationId: conversationId,
                  amount: activeEscrow.amount,
                  sellerId: sellerId,
                  buyerId: buyerId,
                  listingTitle: listingTitle || 'Item',
                  fromChat: true,
                  chatState: locationState
                }
              })}
              disabled={processingAction}
              className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <AlertTriangle className="w-5 h-5" />
              Report Issue / Raise Dispute
            </button>
          </div>
        );
      }
    
    case 'disputed':
      return (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Dispute Active</p>
                <p className="text-sm text-red-700 mt-1">
                  This transaction is under review by our support team.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate(`/disputes/${activeEscrow.dispute_id || activeEscrow.escrow_id}`, {
              state: {
                fromChat: true,
                conversationId: conversationId,
                chatState: locationState
              }
            })}
            className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <AlertTriangle className="w-5 h-5" />
            View Dispute Details
          </button>
        </div>
      );
    
    case 'released':
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">Transaction Completed</p>
              <p className="text-sm text-green-700 mt-1">
                {isCurrentUserBuyer
                  ? 'Payment released to seller. Thank you for using escrow!'
                  : 'Payment has been released to your account.'}
              </p>
            </div>
          </div>
        </div>
      );
    
    default:
      return null;
  }
};

export default EscrowActions;